const NOTHING = { updates: {}, populatedFields: [] };
/**
 * SOR-173 — route an extraction's case category onto wizard state.
 *
 * The single rule both `applyExtraction` write sites apply: the client one in
 * `EFilingContext` and the server one in `orderService`. They used to carry
 * two hand-mirrored copies, which is how the server came to drop the candidate
 * set entirely while the client carried it. Keeping the rule here means a
 * change to it cannot land on one path only.
 *
 * The rule turns on ONE question — did the extracted name resolve to one of
 * the court's own case-category codes?
 *
 * - **Resolved** (`code` non-empty, which the server-side resolver fills, and
 *   which the foreign-category recovery also fills after adopting the court's
 *   own label into `name`): this IS the filing's case category. It goes to
 *   `caseCategoryName` — the one value every category display surface reads —
 *   and is recorded as OCR-populated.
 * - **Rejected** (`code` empty): the court does not file under that name. The
 *   name is then only ever a QUOTE of what the document said, which the
 *   recovery banner uses to explain the rejection, so it goes to the
 *   banner-only field and `caseCategoryName` is left untouched. It is
 *   deliberately NOT recorded as OCR-populated: it is not a value the filer
 *   confirms as their category.
 *
 * Both recovery-state fields — the banner quote and the candidate set — are
 * written on EVERY routed extraction, empty included, so neither can outlive
 * the resolution that produced it. Writing them only when non-empty made the
 * rule incapable of REPLACING a stale value: a second rejected extraction on
 * the same order whose shared-parent intersection is empty (nothing to offer)
 * left the previous resolution's chips rendered under a banner quoting the new
 * name, which is not "exactly the candidates this resolution carries". The
 * candidates belong to the resolution, not to either outcome of it, which is
 * why they are written in both branches.
 */
export function routeExtractedCaseCategory(caseCategory) {
    if (!caseCategory?.name)
        return NOTHING;
    const updates = {};
    const populatedFields = [];
    if (caseCategory.code) {
        updates.caseCategoryName = caseCategory.name;
        updates.caseCategoryCode = caseCategory.code;
        // Nothing was rejected, so no quote survives this extraction.
        updates.ocrExtractedCategoryName = undefined;
        populatedFields.push('caseCategoryName', 'caseCategoryCode');
    }
    else {
        updates.ocrExtractedCategoryName = caseCategory.name;
    }
    updates.caseCategoryCandidates = caseCategory.candidates?.length
        ? caseCategory.candidates
        : undefined;
    return { updates, populatedFields };
}
/**
 * SOR-173 — normalize a hydrating draft's case category onto the routed shape.
 *
 * A draft persisted BEFORE the routing rule above landed carries a
 * court-rejected name in `caseCategoryName` — the old write site copied it
 * there unconditionally — with the banner-only field absent. Every category
 * display surface reads `caseCategoryName` and would state the rejected name as
 * the filing's category, while the recovery banner, which keys on the declared
 * field, would not render at all: the pre-change wizard at least derived the
 * rejection by re-running the matcher and did show the banner, so leaving these
 * drafts alone is a regression on exactly the state the incident is about.
 *
 * Applying the move at hydration — the one chokepoint every draft passes
 * through, alongside the `withDefaultSource` roster backfill — converges legacy
 * state onto the shape a fresh extraction produces, so every reader downstream
 * sees one state shape and no reader needs a legacy branch. No data migration:
 * the wizard's own save re-persists the normalized values.
 *
 * The antecedent is narrow on purpose. A name with NO code is only ever an
 * extraction the court did not accept — every other writer sets the pair
 * together (both in-wizard category pickers, the case-attach backfill, which
 * fills the name only once the code is known), so nothing else is caught. An
 * already-normalized draft (banner field present) is left exactly as it is.
 *
 * `caseCategoryName` also drops out of `ocrPopulatedFields`, matching the rule
 * above: a rejected name is not a value the filer confirms as their category,
 * so the confirm card must not offer it as one read from the documents.
 */
export function backfillRejectedCategoryName(caseInfo, ocrPopulatedFields) {
    const populated = [...(ocrPopulatedFields ?? [])];
    if (caseInfo.caseCategoryCode || !caseInfo.caseCategoryName || caseInfo.ocrExtractedCategoryName) {
        return { caseInfo, ocrPopulatedFields: populated };
    }
    return {
        caseInfo: {
            ...caseInfo,
            caseCategoryName: '',
            ocrExtractedCategoryName: caseInfo.caseCategoryName,
        },
        ocrPopulatedFields: populated.filter(field => field !== 'caseCategoryName'),
    };
}
const NOTHING_TYPE = { updates: {}, populatedFields: [] };
/**
 * SOR-194 — route an extraction's case TYPE onto wizard state.
 *
 * Same rule, same reason, same two write sites as `routeExtractedCaseCategory`
 * above: SOR-173 unified the CATEGORY and left the type hand-mirrored at both
 * sites, so the echo it removed from the category display survived on the type
 * one. Walter's 2026-08-24 Madera run read "Unlawful Detainer" off a UD
 * complaint; the court publishes two types under that name (Residential /
 * Commercial), so the resolver correctly declined to pick one — and the card
 * header and the Filing Summary both stated `Type: Unlawful Detainer` directly
 * above "We couldn't read the case type from your document."
 *
 * The rule turns on the same ONE question — did the extracted name resolve to
 * one of the court's own case-type codes?
 *
 * - **Resolved** (`code` non-empty): this IS the filing's case type. It goes to
 *   `caseType`, the pair every type display surface reads, and both halves are
 *   recorded as OCR-populated.
 * - **Rejected** (`code` empty): the name matched none of this court's types,
 *   or matched several equally well. It is then only ever a QUOTE of what the
 *   document said — the recovery copy's material, not the filing's type — so it
 *   goes to the banner-only field and `caseType` is left ENTIRELY untouched.
 *   Untouched, not cleared: a type the filer already picked (or an earlier
 *   document resolved) outranks a later reading the court rejects, matching the
 *   category rule. Nothing is recorded as OCR-populated, because a rejected
 *   name is not a value the filer confirms as their type.
 */
export function routeExtractedCaseType(caseType) {
    if (!caseType?.name)
        return NOTHING_TYPE;
    const updates = {};
    const populatedFields = [];
    if (caseType.code) {
        updates.caseType = { code: caseType.code, name: caseType.name };
        // Nothing was rejected, so no quote survives this extraction.
        updates.ocrExtractedCaseTypeName = undefined;
        populatedFields.push('caseTypeName', 'caseTypeCode');
    }
    else {
        updates.ocrExtractedCaseTypeName = caseType.name;
    }
    return { updates, populatedFields };
}
/**
 * SOR-194 — normalize a hydrating draft's case type onto the routed shape.
 *
 * The type twin of `backfillRejectedCategoryName`, for the same reason: a draft
 * persisted BEFORE the routing above landed carries the court-rejected name in
 * `caseType.name` with an empty `code`, because the old write site copied it
 * there unconditionally. Read back unchanged, the confirm card and the Filing
 * Summary would go on stating that name as the filing's type — the very echo
 * this change removes — for every draft already saved.
 *
 * Applying the move at hydration, the one chokepoint every draft passes
 * through, converges legacy state onto the shape a fresh extraction now
 * produces, so no reader downstream needs a legacy branch. No data migration:
 * the wizard's own save re-persists the normalized values.
 *
 * The antecedent is narrow on purpose. A `caseType` name with NO code is only
 * ever an extraction the court did not accept — every other writer sets the
 * pair together (both type pickers write a matched `{code, name}` or `null`) —
 * so nothing else is caught, and an already-normalized draft (banner field
 * present) is left exactly as it is. `caseType` goes to `null` rather than to a
 * blank pair: `null` is the shape the wizard's own "no type yet" state has, and
 * `caseTypeName` drops out of `ocrPopulatedFields` for the same reason the
 * category's does — the confirm card must not offer a rejected name as a value
 * read from the documents.
 */
export function backfillRejectedCaseTypeName(caseInfo, ocrPopulatedFields) {
    const populated = [...(ocrPopulatedFields ?? [])];
    if (!caseInfo.caseType?.name || caseInfo.caseType.code || caseInfo.ocrExtractedCaseTypeName) {
        return { caseInfo, ocrPopulatedFields: populated };
    }
    return {
        caseInfo: {
            ...caseInfo,
            caseType: null,
            ocrExtractedCaseTypeName: caseInfo.caseType.name,
        },
        ocrPopulatedFields: populated.filter(field => field !== 'caseTypeName'),
    };
}
